import React, { JSX, useState, useCallback } from 'react';
import { Field, RichText } from '@sitecore-content-sdk/nextjs';
import { tv } from 'tailwind-variants';

import { SvgIcon } from 'helpers/SvgIcon';
import useDictionary from 'lib/hooks/useDictionary';
import { ProcessedProviderReviews } from '../../../types/pg-provider-types';
import { usePageData } from 'lib/utils/page-data-static-props/pageDataContext';
import { Result } from '@coveo/headless';

// Constants
const MINIMUM_RATING_COUNT = 30;
const INITIAL_VISIBLE_COMMENTS = 5;
const MAX_VISIBLE_COMMENTS = 20;
const COMMENTS_INCREMENT = 5;

/** Defines the available presentation variants for the component. */
export type ReviewDisplayVariant = 'large-profile' | 'search-card' | 'full-details';

type ProviderReviewsProps = {
  npi?: Field<string>;
  whyNoRatings?: Field<string>;
  variant: ReviewDisplayVariant;
  /** * For 'search-card': The full URL of the provider profile page.
   * For 'large-profile' & 'full-details': The anchor ID for the full reviews section.
   */
  profilePageUrlOrAnchorId?: string;
  coveoSearchData?: Result;
  providerURL?: string;
};

const getStarIcons = (rating: number): ('star' | 'star-half' | 'star-outline')[] => {
  const starIcons: ('star' | 'star-half' | 'star-outline')[] = [];
  let remainingRating = rating;

  for (let i = 0; i < 5; i++) {
    if (remainingRating >= 1) {
      starIcons.push('star');
      remainingRating -= 1;
    } else if (remainingRating > 0.4) {
      starIcons.push('star-half');
      remainingRating = 0;
    } else {
      starIcons.push('star-outline');
    }
  }
  return starIcons;
};

const reviewStyles = tv({
  slots: {
    ratingWrapper: ['flex'],
    ratingNumber: ['font-centuryGothic', 'font-bold'],
    reviewCount: ['font-nunitoSans', 'text-gray-700'],
    jumpLink: ['text-blue-700', 'underline', 'mt-4'],
    whyNoRatingsWrapper: [
      'w-full',
      '[&_h3]:font-centuryGothic [&_h3]:font-bold [&_h3]:text-22 [&_h3]:leading-25 [&_h3]:mb-12 [&_h3]:text-theme-text',
      '[&_p]:font-nunitoSans [&_p]:text-16 [&_p]:leading-22 [&_p]:text-gray-900 [&_p]:mb-12',
      '[&_ol]:list-decimal [&_ol]:pl-8 [&_ol]:mb-12 [&_ul]:list-disc [&_ul]:pl-8 [&_ul]:mb-12',
      '[&_li]:font-nunitoSans [&_li]:text-16 [&_li]:leading-22 [&_li]:text-gray-900 [&_li]:mb-4 [&_strong]:font-bold',
    ],
  },
  variants: {
    displayVariant: {
      'large-profile': {
        ratingWrapper: ['gap-4', 'flex-col', 'items-start'],
        ratingNumber: ['text-48', 'leading-none', 'text-theme-text'],
        reviewCount: ['text-20'],
      },
      'search-card': {
        ratingWrapper: ['gap-1'],
        ratingNumber: ['text-16', 'text-gray-900'],
        reviewCount: ['text-14', 'ml-2', 'border-l', 'border-gray-300', 'pl-2'],
      },
      'full-details': {
        ratingWrapper: ['gap-2', 'mb-2'],
        ratingNumber: ['text-28', 'text-theme-text'],
        reviewCount: ['text-18'],
      },
    },
  },
});

type LargeProfileProps = {
  reviews: ProcessedProviderReviews;
  linkOrAnchorId?: string;
  getDictionaryValue: (key: string) => string;
};

const ProviderReviewsLargeProfile = ({
  reviews,
  linkOrAnchorId,
  getDictionaryValue,
}: LargeProfileProps) => {
  const { totalCommentCount, overallRating } = reviews;
  const formattedRating = overallRating.toFixed(1);
  const starIcons = getStarIcons(overallRating);
  const { jumpLink } = reviewStyles({ displayVariant: 'large-profile' });

  const ariaRatingLabel = `${formattedRating} / 5 ${getDictionaryValue('stars')}, ${totalCommentCount} ${getDictionaryValue('Reviews')}`;

  if (reviews.totalRatingCount >= MINIMUM_RATING_COUNT) {
    return (
      <div className="mt-8">
        <div className="flex items-center gap-8">
          {/* Visually hidden text for screen readers */}
          <span className="sr-only">{ariaRatingLabel}</span>

          <div className="flex-shrink-0">
            <p className="font-centuryGothic font-bold text-48 leading-none text-theme-text">
              {formattedRating}
            </p>
          </div>
          <div className="flex flex-col items-start gap-2">
            {/* Visual stars hidden from screen readers to prevent repetition */}
            <div className="flex gap-1" aria-hidden="true">
              {starIcons.map((icon, i) => (
                <SvgIcon
                  key={`${icon}-${i}`}
                  icon={icon}
                  className={`!h-[18px] !w-[18px] [&>path]:fill-blue-700`}
                  size="custom"
                />
              ))}
            </div>
            {linkOrAnchorId && totalCommentCount > 0 && (
              <a href={`#${linkOrAnchorId}`} className={`${jumpLink()} text-base underline`}>
                {totalCommentCount} {getDictionaryValue('Reviews')}
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }
  return null;
};

type FullDetailsProps = {
  reviews?: ProcessedProviderReviews;
  whyNoRatings?: Field<string>;
  getDictionaryValue: (key: string) => string;
  whyNoRatingsWrapper: string;
};

const ProviderReviewsFullDetails = ({
  reviews,
  whyNoRatings,
  getDictionaryValue,
  whyNoRatingsWrapper,
}: FullDetailsProps) => {
  const [visibleComments, setVisibleComments] = useState(INITIAL_VISIBLE_COMMENTS);

  const handleShowMore = useCallback(() => {
    setVisibleComments((prev) => Math.min(prev + COMMENTS_INCREMENT, MAX_VISIBLE_COMMENTS));
  }, []);
  const hasEnoughData = reviews && reviews.totalRatingCount >= MINIMUM_RATING_COUNT;

  if (!hasEnoughData) {
    if (whyNoRatings?.value) {
      return (
        <div className={whyNoRatingsWrapper}>
          <RichText field={whyNoRatings} />
        </div>
      );
    }
    return null;
  }

  const { overallRating, comments } = reviews;

  const formattedRating = overallRating.toFixed(1);
  const starIcons = getStarIcons(overallRating);
  const hasEnoughRatings = reviews.totalRatingCount >= MINIMUM_RATING_COUNT;

  const displayedComments = comments.slice(0, visibleComments);
  const hasMoreToShow = visibleComments < comments.length && visibleComments < MAX_VISIBLE_COMMENTS;

  return (
    <div className="w-full">
      {hasEnoughRatings && (
        <div className="bg-blue-50 mb-[40px] rounded-eight md:p-12 p-6">
          <div className="flex md:flex-row flex-col-reverse md:items-center items-start md:gap-8 gap-4">
            <div className="flex-shrink-0 flex flex-col gap-2 items-center">
              <p className="font-centuryGothic font-bold text-48 leading-none text-theme-text">
                {formattedRating}
              </p>
              <div className="flex gap-1">
                {starIcons.map((icon, i) => (
                  <SvgIcon
                    key={`${icon}-${i}`}
                    icon={icon}
                    className={`!h-[18px] !w-[18px] [&>path]:fill-blue-700`}
                    size="custom"
                  />
                ))}
              </div>
            </div>

            <p className="font-nunitoSans text-[26px] font-bold leading-[32px] text-theme-subtext md:p-6 p-0 md:text-left text-left md:flex-grow">
              {getDictionaryValue('OverallParentAndPatientRating')}
            </p>
          </div>
        </div>
      )}

      {comments.length > 0 && (
        <div>
          <h3 className="text-[22px] font-bold mb-12">
            {getDictionaryValue('SeeWhatParentsAndPatientsAreSaying')}
          </h3>

          <div className="space-y-12 px-6 md:px-0">
            {displayedComments.map((comment, index) => {
              const commentStars = getStarIcons(comment.pgCommentRating);
              return (
                <div key={index} className="border-b border-gray-50 last:border-b-0">
                  <div className="flex items-start gap-4 mb-8">
                    <div className="flex gap-1">
                      {commentStars.map((icon, i) => (
                        <SvgIcon
                          key={`${icon}-${i}`}
                          icon={icon}
                          className={`!h-[18px] !w-[18px] [&>path]:fill-blue-700`}
                          size="custom"
                        />
                      ))}
                    </div>
                    <span className="font-nunitoSans text-14 text-theme-subtext font-bold">
                      {new Date(comment.pgCommentDate).toLocaleDateString('en-US')}
                    </span>
                  </div>

                  <p className="font-nunitoSans text-16 text-gray-900 leading-relaxed mb-12">
                    {comment.pgComment}
                  </p>
                </div>
              );
            })}
          </div>

          {hasMoreToShow && (
            <button
              onClick={handleShowMore}
              className="mt-6 text-blue-base font-nunitoSans text-18 font-bold flex items-center gap-4 hover:underline px-6 md:px-0"
            >
              {getDictionaryValue('ShowMoreReviews')}
              <svg
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19.5938 11C19.5938 11.2735 19.4851 11.5358 19.2917 11.7292C19.0983 11.9226 18.836 12.0312 18.5625 12.0312H12.0312V18.5625C12.0312 18.836 11.9226 19.0983 11.7292 19.2917C11.5358 19.4851 11.2735 19.5938 11 19.5938C10.7265 19.5938 10.4642 19.4851 10.2708 19.2917C10.0774 19.0983 9.96875 18.836 9.96875 18.5625V12.0312H3.4375C3.164 12.0312 2.90169 11.9226 2.7083 11.7292C2.5149 11.5358 2.40625 11.2735 2.40625 11C2.40625 10.7265 2.5149 10.4642 2.7083 10.2708C2.90169 10.0774 3.164 9.96875 3.4375 9.96875H9.96875V3.4375C9.96875 3.164 10.0774 2.90169 10.2708 2.7083C10.4642 2.5149 10.7265 2.40625 11 2.40625C11.2735 2.40625 11.5358 2.5149 11.7292 2.7083C11.9226 2.90169 12.0312 3.164 12.0312 3.4375V9.96875H18.5625C18.836 9.96875 19.0983 10.0774 19.2917 10.2708C19.4851 10.4642 19.5938 10.7265 19.5938 11Z"
                  fill="#0074C8"
                />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const ProviderReviews = ({
  whyNoRatings,
  variant,
  profilePageUrlOrAnchorId: linkOrAnchorId,
  coveoSearchData,
  providerURL,
}: ProviderReviewsProps): JSX.Element | null => {
  const { getDictionaryValue } = useDictionary();
  const { whyNoRatingsWrapper } = reviewStyles();

  const { pageData } = usePageData<{ ratingsAndReviews?: ProcessedProviderReviews }>();

  const providerReviews = pageData?.ratingsAndReviews;

  switch (variant) {
    case 'large-profile':
      if (!providerReviews || providerReviews.totalRatingCount < MINIMUM_RATING_COUNT) {
        return null;
      }
      return (
        <ProviderReviewsLargeProfile
          reviews={providerReviews}
          linkOrAnchorId={linkOrAnchorId}
          getDictionaryValue={getDictionaryValue}
        />
      );

    case 'search-card':
      if (
        !coveoSearchData?.raw?.cwpgoverallrating ||
        parseInt(String(coveoSearchData?.raw?.cwpgtotalratingcount ?? '0')) < MINIMUM_RATING_COUNT
      ) {
        return null;
      }
      // Extract values from coveoSearchData
      const formattedRating = (coveoSearchData?.raw?.cwpgoverallrating as number).toFixed(1);
      const starIcons = getStarIcons(coveoSearchData?.raw?.cwpgoverallrating as number);
      const { jumpLink } = reviewStyles({ displayVariant: 'search-card' });
      const totalCommentCount = parseInt(
        String(coveoSearchData?.raw?.cwpgtotalcommentcount ?? '0')
      );
      const ariaRatingLabel = `${formattedRating} / 5 ${getDictionaryValue('stars')}, ${totalCommentCount} ${getDictionaryValue('Reviews')}`;
      const linkOrAnchor =
        linkOrAnchorId && coveoSearchData?.raw?.cwpressganeyurl
          ? `${coveoSearchData?.raw?.cwpressganeyurl}#${linkOrAnchorId}`
          : linkOrAnchorId && providerURL
            ? `${providerURL}#${linkOrAnchorId}`
            : null;
      return (
        <>
          <div className="mt-8">
            <div className="flex items-center gap-8">
              {/* Visually hidden text for screen readers */}
              <span className="sr-only">{ariaRatingLabel}</span>
              <div className="flex flex-row gap-4 mt-3 mb-3 items-center">
                {/* Visual stars hidden from screen readers to prevent repetition */}
                <span className="flex gap-1" aria-hidden="true">
                  {starIcons.map((icon, i) => (
                    <SvgIcon
                      key={`${icon}-${i}`}
                      icon={icon}
                      className={`!h-[18px] !w-[18px] [&>path]:fill-blue-700`}
                      size="custom"
                    />
                  ))}
                </span>
                <span className="flex-shrink-0">
                  <p className="font-centuryGothic text-16 leading-none text-theme-text">
                    {formattedRating}
                  </p>
                </span>
                <span className="border-l-2 border-gray-400 pl-4">
                  {linkOrAnchor && totalCommentCount > 0 && (
                    <a href={linkOrAnchor} className={`${jumpLink()} text-base underline`}>
                      {totalCommentCount} {getDictionaryValue('Reviews')}
                    </a>
                  )}
                </span>
              </div>
            </div>
          </div>
        </>
      );

    case 'full-details':
      return (
        <ProviderReviewsFullDetails
          reviews={providerReviews}
          whyNoRatings={whyNoRatings}
          getDictionaryValue={getDictionaryValue}
          whyNoRatingsWrapper={whyNoRatingsWrapper()}
        />
      );

    default:
      return null;
  }
};

export default ProviderReviews;
